import type { EnvironmentId } from "@cmmd-forge/contracts";

import type { PullRequestRightPanelSurface } from "../../rightPanelSurface";
import { usePullRequestInfiniteQueries } from "./usePullRequestInfiniteQueries";
import type { PullRequestsSearch } from "./pullRequests.logic";
import { aggregatePullRequestInbox } from "./pullRequests.inbox.logic";

const WARM_SNAPSHOT_SEARCH_KEYS = [
  "involvement",
  "environmentId",
  "projectId",
  "host",
  "provider",
  "repository",
  "number",
  "query",
] as const;

export function rowMatchesPullRequestSearch(
  row: {
    readonly projectId: PullRequestsSearch["projectId"];
    readonly provider: string;
    readonly host: string;
    readonly repository: string;
    readonly number: number;
  },
  search: PullRequestsSearch,
): boolean {
  return [
    new Set([undefined, row.projectId]).has(search.projectId),
    new Set([undefined, row.provider]).has(search.provider),
    new Set([undefined, row.host]).has(search.host),
    new Set([undefined, row.repository]).has(search.repository),
    new Set([undefined, row.number]).has(search.number),
  ].every(Boolean);
}

export function isDefaultPullRequestInboxSearch(search: PullRequestsSearch): boolean {
  return (
    search.state === "open" && WARM_SNAPSHOT_SEARCH_KEYS.every((key) => search[key] === undefined)
  );
}

export function pullRequestInboxState(input: {
  readonly environmentCount: number;
  readonly queryCount: number;
  readonly aggregateState: ReturnType<typeof aggregatePullRequestInbox>["state"];
}) {
  if (input.environmentCount === 0) return "unavailable" as const;
  if (input.queryCount !== input.environmentCount) return "loading" as const;
  return input.aggregateState;
}

export function activeSurfaceMatchesPullRequestRow(
  surface: PullRequestRightPanelSurface | null,
  row: {
    readonly provider: string;
    readonly environmentId: EnvironmentId;
    readonly host: string;
    readonly repository: string;
    readonly number: number;
  },
): boolean {
  if (surface === null) return false;
  return [
    surface.provider === row.provider,
    surface.environmentId === row.environmentId,
    surface.host === row.host,
    surface.repository === row.repository,
    surface.number === row.number,
  ].every(Boolean);
}

type PullRequestInfiniteQuery = ReturnType<typeof usePullRequestInfiniteQueries>[number];

function firstPullRequestPage(query: PullRequestInfiniteQuery) {
  return query.data?.pages[0];
}

const EMPTY_PULL_REQUEST_PAGE = { entries: [], viewers: {}, errors: [] } as const;

function pullRequestInboxResult(query: PullRequestInfiniteQuery, environmentId: EnvironmentId) {
  const page = firstPullRequestPage(query) ?? EMPTY_PULL_REQUEST_PAGE;
  return {
    environmentId,
    entries: page.entries,
    viewers: page.viewers,
    responseErrors: page.errors,
    isPending: query.isPending,
    error: query.error,
  };
}

export function aggregatePullRequestQueries(
  queries: ReadonlyArray<PullRequestInfiniteQuery>,
  environments: ReadonlyArray<{ readonly environmentId: EnvironmentId }>,
) {
  return aggregatePullRequestInbox(
    queries.map((query, index) =>
      pullRequestInboxResult(query, environments[index]!.environmentId),
    ),
  );
}
